import { forwardRef } from "react";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Badge } from "../../../../../components/ui/badge";
import { OptroWorkStepSummary } from "../../../../../models/audit/workStep";

const EMPTY_VALUE = "–";

interface SelectWorkStepRowProps {
  workStep: OptroWorkStepSummary;
  checked: boolean;
  onToggle: (workStep: OptroWorkStepSummary) => void;
}

// One selectable row of the import wizard's select-work-steps table. Rendered
// nested under its audit's accordion group, so no audit column repeats here.
// Mirrors SelectControlRow.
export const SelectWorkStepRow = forwardRef<
  HTMLTableRowElement,
  SelectWorkStepRowProps
>(({ workStep, checked, onToggle }, ref) => {
  // Imported rows, rows being imported right now, and rows whose Optro
  // status freezes the testing strategy (Reviewed/Complete/Not In Scope)
  // list but can't be selected; the badge names why.
  const disabled =
    workStep.linked || workStep.importing || workStep.untestableStatus !== null;
  const badgeLabel = workStep.linked
    ? "Imported"
    : workStep.importing
      ? "Importing"
      : workStep.untestableStatus;
  return (
    <TableRow
      ref={ref}
      className={disabled ? "opacity-50" : "cursor-pointer"}
      onClick={() => {
        if (!disabled) onToggle(workStep);
      }}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={checked}
          disabled={disabled}
          onCheckedChange={() => onToggle(workStep)}
          aria-label={`Select ${workStep.externalRef}`}
        />
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2 pl-6">
          <span>
            {workStep.externalRef} - {workStep.name}
          </span>
          {badgeLabel && (
            <Badge variant="outline" className="whitespace-nowrap">
              {badgeLabel}
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-stone-600">
        {workStep.section ?? EMPTY_VALUE}
      </TableCell>
      <TableCell className="text-stone-600">
        {workStep.subsection ?? EMPTY_VALUE}
      </TableCell>
      <TableCell className="text-stone-600">
        {workStep.preparer ?? EMPTY_VALUE}
      </TableCell>
    </TableRow>
  );
});

SelectWorkStepRow.displayName = "SelectWorkStepRow";
